import { useCallback, useEffect, useRef, useState } from 'react';
import TeamCard from './TeamCard';

function getSlidesPerView(width) {
  if (width <= 640) return 1;
  if (width <= 960) return 2;
  return 3;
}

export default function TeamCarousel({ members = [] }) {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(3);
  const maxIndex = Math.max(0, members.length - slidesPerView);

  const updateSlidesPerView = useCallback(() => {
    setSlidesPerView(getSlidesPerView(window.innerWidth));
  }, []);

  const syncActiveIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;

    const slideWidth = track.children[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    const index = Math.round(track.scrollLeft / (slideWidth + gap));
    setActiveIndex(Math.min(index, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
    return () => window.removeEventListener('resize', updateSlidesPerView);
  }, [updateSlidesPerView]);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    track.addEventListener('scroll', syncActiveIndex, { passive: true });
    return () => track.removeEventListener('scroll', syncActiveIndex);
  }, [syncActiveIndex, members.length]);

  function scrollToIndex(index) {
    const track = trackRef.current;
    if (!track || !track.children[index]) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.children[index].scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      inline: 'start',
      block: 'nearest',
    });
    setActiveIndex(index);
  }

  function goPrev() {
    scrollToIndex(Math.max(0, activeIndex - 1));
  }

  function goNext() {
    scrollToIndex(Math.min(maxIndex, activeIndex + 1));
  }

  function handleTrackKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrev();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext();
    }
  }

  if (!members.length) return null;

  return (
    <div className="team-carousel" role="region" aria-roledescription="carousel" aria-label="Equipo">
      <div className="team-carousel__header">
        <button
          type="button"
          className="team-carousel__arrow"
          aria-label="Miembro anterior"
          onClick={goPrev}
          disabled={activeIndex === 0}
        >
          ‹
        </button>
        <button
          type="button"
          className="team-carousel__arrow"
          aria-label="Siguiente miembro"
          onClick={goNext}
          disabled={activeIndex >= maxIndex}
        >
          ›
        </button>
      </div>

      <div
        ref={trackRef}
        className="team-carousel__track"
        tabIndex={0}
        onKeyDown={handleTrackKeyDown}
      >
        {members.map((member, index) => (
          <div
            key={member.id}
            className="team-carousel__slide"
            role="group"
            aria-label={`${index + 1} de ${members.length}`}
          >
            <TeamCard member={member} />
          </div>
        ))}
      </div>

      <div className="team-carousel__dots" role="tablist" aria-label="Seleccionar miembro">
        {Array.from({ length: maxIndex + 1 }, (_, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            className={`team-carousel__dot${index === activeIndex ? ' team-carousel__dot--active' : ''}`}
            aria-label={`Ir al miembro ${index + 1}`}
            aria-selected={index === activeIndex}
            onClick={() => scrollToIndex(index)}
          />
        ))}
      </div>
    </div>
  );
}
